import { useEffect, useMemo, useState } from "react";
import { STATE, CART_STATES, SHIPPING_COSTS, PMT } from "../classes/Enum";
import "../styles/cart.css";
import CartItems from "./cart/CartItems";
import { useAppState, useCart } from "./Context";
import { getDisplayPrice } from "../helper/products";
import Checkout from "./cart/Checkout";
import SuccessComponent from "./Success";
import { getButtonText, prevCartState, nextCartState } from "../helper/addToCart";
import Spinner from "./Spinner";
import { buy } from "../api/purchase";

function Cart({ fetchProduct, stripePromise, isMobile}) {
  const { setState } = useAppState();
  const [cartState, setCartState] = useState(CART_STATES.ITEMS);
  const { cart, setCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState(PMT.CARD);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [checkoutMeta, setCheckoutMeta] = useState({
    shippingCost: null,
    couponCode: "",
    discountAmount: 0,
    flatDiscount: 0,
    orderId: null,
    readyToCheckout: false,
    paymentFilled: false
  });

  const [customerInfo, setCustomerInfo] = useState({
    tiktokName: "",
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    shippingSuburb: "",
    shippingPostcode: "",
    shippingState: "",
    shippingCountry: "",
    billingAddress: "",
    billingSuburb: "",
    billingPostcode: "",
    billingState: "",
    billingCountry: "",
  });

  const cartCount = useMemo(() => {
    return cart.reduce((count, cartItem) => count + (cartItem.count || 0), 0);
  }, [cart]);

  const itemsSubtotal = useMemo(() => {
    return cart.reduce((total, cartItem) => {
      const { productId, condition, variant, count } = cartItem;
      const item = fetchProduct(productId);
      if (!item) return total;

      if (cartItem.appliedPrice != null && cartItem.appliedPrice !== "") {
        return total + Number(cartItem.appliedPrice) * (count || 0);
      }

      let price = 0;
      if (item.type === STATE.CARD) {
        price = item.conditionQuantities?.[variant]?.[condition]?.price || 0;
      } else {
        price = Number(item.price) || 0;
      }

      return total + price * (count || 0);
    }, 0);
  }, [cart, fetchProduct]);

  const grandTotal = useMemo(() => {
    return Math.max(0, itemsSubtotal + (checkoutMeta.shippingCost || 0) - (checkoutMeta.flatDiscount || 0));
  }, [itemsSubtotal, checkoutMeta.shippingCost, checkoutMeta.flatDiscount]);

const handleRight = () => {

     if (cartState === CART_STATES.SUCCESS) {
      setCart([]);
      setCheckoutMeta({
        shippingCost: SHIPPING_COSTS.STANDARD,
        couponCode: "",
        discountAmount: 0,
        flatDiscount: 0,
        orderId: null,
      });
      setState(STATE.OVERVIEW);
    } else if (cartState === CART_STATES.CHECKOUT) {
        if (checkoutMeta.readyToCheckout === false) {
          setError("Please fill in all required fields to proceed.");
          return;
        }
        if (grandTotal <= 0) {
          checkoutFree();
          return;
        }
        if (paymentMethod === PMT.CARD && checkoutMeta.paymentFilled) {
          const form = document.getElementById("payment-element-form");
          form?.requestSubmit();
        }
        else if (paymentMethod === PMT.PAYID) {
          checkoutWithPayID();
        }
    } else {
      setCartState((prev) => nextCartState(prev));
    }
  }

  const handleCheckoutMetaChange = (next) => {
    setCheckoutMeta((prev) => ({
      ...prev,
      ...(next || {}),
    }));
  };

  async function checkoutFree() {
      setLoading(true);
      setError("");
      try {
        const res = await buy(
          grandTotal,
          cart,
          paymentMethod || PMT.CARD,
          customerInfo,
          checkoutMeta.shippingCost ?? 0,
          checkoutMeta.couponCode || ""
        );
        if (!res.success) {
          setError(res.error || "Failed to complete order.");
          return;
        }
        setCartState(CART_STATES.SUCCESS);
        handleCheckoutMetaChange({ orderId: res.orderId });
        localStorage.removeItem("cart");
      } finally {
        setLoading(false);
      }
  }

  async function checkoutWithPayID() {
      const res = await buy(grandTotal, cart, PMT.PAYID, customerInfo, checkoutMeta.shippingCost, checkoutMeta.couponCode || '');
      setCartState(CART_STATES.SUCCESS);
      console.log(res);
      handleCheckoutMetaChange({orderId:res.orderId});
  }

  const handleLeft = () => {
    setCartState((prev) => prevCartState(prev));
    if (cartState === CART_STATES.ITEMS) {
      setState(STATE.OVERVIEW);
    }
  }

  const isOnItems = cartState === CART_STATES.ITEMS;
  const isOnCheckout = cartState === CART_STATES.CHECKOUT;
  const isOnSuccess = cartState === CART_STATES.SUCCESS;

  useEffect(() => {
    setError(null);
  }, [cartState])

  return (
    <div id="cart-container">
      <CartItems cart={cart} setCart={setCart} fetchProduct={fetchProduct} minimised={!isOnItems} isMobile={isMobile}/>
      {(isOnCheckout && isMobile) && <div className="item-divider-long full"/>}
      {isOnCheckout && (
        <Checkout
          // parent stays the source of truth for totals
          itemsSubtotal={itemsSubtotal}
          grandTotal={grandTotal} // if your Checkout expects totalPrice
          stripePromise={stripePromise}
          cartState={cartState}
          setCartState={setCartState}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          checkoutMeta={checkoutMeta}
          onCheckoutMetaChange={handleCheckoutMetaChange}
          setError={setError}
          setLoading={setLoading}
          setCart={setCart}
          customerInfo={customerInfo}
          setCustomerInfo={(next) => {
            setCustomerInfo((prev) => ({...prev, ...(next || {})}))}
          }
        />
      )}

      {isOnSuccess && (
        <div>
          <div className="item-divider-long"/>
        <SuccessComponent transactionId={checkoutMeta.orderId} totalPrice={grandTotal} cart={cart} fetchProduct={fetchProduct} setCart={setCart}
        paymentMethod={paymentMethod} email={customerInfo.email}/>
        </div>
      )}

        <div className="cart-button-container spread ">
          <button
            className={`cart-button left tertiary-active`}
            onClick={handleLeft}
            style={cartState === CART_STATES.SUCCESS ? { display: "none" } : {}}
          >
            {getButtonText(cartState, "left")}
          </button>
          <div className="cart-details-container">
            <div className="cart-details"
              style={cartState === CART_STATES.SUCCESS ? { display: "none" } : {}}
            >
              {(cartState === CART_STATES.ITEMS) && 
              <div>
                <div>${getDisplayPrice(itemsSubtotal)}</div>
                <div>{cartCount}{" "}
                {cartCount === 1 ? "item" : "items"}
                </div>
              </div>
              }
              {(cartState === CART_STATES.CHECKOUT) &&
              <div style={{ opacity: 0.9 }}>
                Total: ${getDisplayPrice(grandTotal)}
              </div>
              }
            </div>
            <div className="cart-error">{error}</div>
          </div>

          {(cart.length > 0) &&
            (!loading ?
            <button
              className={`cart-button right is-active`}
              onClick={handleRight}
            > {getButtonText(cartState, "right", paymentMethod, grandTotal)}
            </button> 
            : <Spinner/>)
          }
        </div>
    </div>
  );
}

export default Cart;
